import React, { useState, useEffect, useMemo, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserAccess } from '../hooks/useUserAccess';
import MyPortalPage from './MyPortalPage';
import { MyTeamList } from '../components/widgets/MyTeamList';
import { MyTeamGoalsList } from '../components/widgets/MyTeamGoalsList';
import { TeamPerformanceTrendline } from '../components/widgets/TeamPerformanceTrendline';
import { NineBoxMatrixWidget } from '../components/widgets/NineBoxMatrixWidget';
import { ReviewCompletionByDeptWidget } from '../components/widgets/ReviewCompletionByDeptWidget';
import { CompetencyGapWidget } from '../components/widgets/CompetencyGapWidget';
import { FeedbackFlowWidget } from '../components/widgets/FeedbackFlowWidget';
import { EngagementScoreTrendsWidget } from '../components/widgets/EngagementScoreTrendsWidget';
import { EngagementPulseRadarWidget } from '../components/widgets/EngagementPulseRadarWidget';
import { RatingCalibrationWidget } from '../components/widgets/RatingCalibrationWidget';
import { TeamLearningPathsList } from '../components/widgets/TeamLearningPathsList';
import { UnitPerformanceChart } from '../components/widgets/UnitPerformanceChart';
import { ReviewProgressChart } from '../components/widgets/ReviewProgressChart';
import { GoalProgressChart } from '../components/widgets/GoalProgressChart';
import { FeedbackActivityChart } from '../components/widgets/FeedbackActivityChart';
import { analyticsService, analyticsFilterService } from '../services/analyticsService';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { useReviewCloudData } from '../hooks/useReviewCloudData';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { useAuth } from '../context/AuthContextBackend';
import {
  TeamCourseCompletionWidget,
  TeamOverdueCoursesWidget,
  TeamEventAttendanceWidget,
  TeamCoachingSnapshotWidget,
  OrgKPISummaryWidget,
  OrgChannelMixWidget,
  OrgTopCoursesWidget,
  OrgCompletionByCategoryWidget,
  OrgTopLearnersWidget,
  OrgILTUtilizationWidget,
  OrgCertificateIssuanceWidget,
} from '../components/widgets/learn';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartColor, getSentimentColor, getChartGridColor } from '@t-one-internal/design-system';

// ============================================
// Shared Spinner (single keyframes definition)
// ============================================
const spinKeyframes = `
@keyframes spin { to { transform: rotate(360deg); } }
.home-dashboard select:focus,
.home-dashboard select:focus-visible {
  outline: none !important;
  box-shadow: none !important;
  border-color: #D1D5DB !important;
}
`;

const WidgetSpinner: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
    <div
      style={{
        width: '24px',
        height: '24px',
        border: '3px solid #E5E7EB',
        borderTopColor: '#4B45E5',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />
  </div>
);

// ============================================
// Tab Definitions
// ============================================
interface TabConfig {
  id: string;
  label: string;
  accessCheck: (flags: {
    isManager: boolean;
    isScopedHRBP: boolean;
    isGlobalHRBP: boolean;
    isExecutive: boolean;
    isSuperAdmin: boolean;
  }) => boolean;
}

const TAB_CONFIG: TabConfig[] = [
  {
    id: 'my-portal',
    label: 'My Portal',
    accessCheck: () => true, // Available to everyone
  },
  {
    id: 'team-overview',
    label: 'Team Overview',
    accessCheck: ({ isManager, isSuperAdmin }) => isManager || isSuperAdmin,
  },
  {
    id: 'unit-overview',
    label: 'Unit Overview',
    accessCheck: ({ isScopedHRBP, isSuperAdmin }) => isScopedHRBP || isSuperAdmin,
  },
  {
    id: 'organization-overview',
    label: 'Organization Overview',
    accessCheck: ({ isGlobalHRBP, isSuperAdmin }) => isGlobalHRBP || isSuperAdmin,
  },
  {
    id: 'executive-overview',
    label: 'Executive Overview',
    accessCheck: ({ isExecutive, isSuperAdmin }) => isExecutive || isSuperAdmin,
  },
];

// ============================================
// My Team Widget (Container)
// ============================================
const MyTeamWidget: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch standard cycles
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);

    analyticsFilterService
      .getFilterOptions('standard_review_cycle_id')
      .then((response) => {
        if (!cancelled && response.options.length > 0) {
          setCycles(response.options);
          setSelectedCycleId(response.options[0].value);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load standard cycles:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch team data when cycle changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsService
      .fetchWidgetData({
        metric_key: 'team_my_team',
        filters: [{ field: 'standard_review_cycle_id', operator: '=', value: selectedCycleId }],
        limit: 1000,
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load team data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
          My Team
        </h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {!loading && !error && (
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
              {data.length} direct report{data.length !== 1 ? 's' : ''}
            </span>
          )}
          {!cyclesLoading && cycles.length > 0 && (
            <select
              value={selectedCycleId}
              onChange={(e) => setSelectedCycleId(e.target.value)}
              style={{
                fontSize: '12px',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '4px 8px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                width: '200px',
              }}
            >
              {cycles.map((cycle) => (
                <option key={cycle.value} value={cycle.value}>
                  {cycle.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'none' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div
              style={{
                width: '24px',
                height: '24px',
                border: '3px solid #E5E7EB',
                borderTopColor: '#4B45E5',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : (
          <MyTeamList data={data} />
        )}
      </div>
    </div>
  );
};

// ============================================
// My Team Goals Widget (Container)
// ============================================
const MyTeamGoalsWidget: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch goal cycles
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);

    analyticsFilterService
      .getFilterOptions('goal_cycle_id')
      .then((response) => {
        if (!cancelled && response.options.length > 0) {
          setCycles(response.options);
          setSelectedCycleId(response.options[0].value);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load goal cycles:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch goal data when cycle changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsService
      .fetchWidgetData({
        metric_key: 'team_my_team_goals',
        filters: [{ field: 'goal_cycle_id', operator: '=', value: selectedCycleId }],
        limit: 1000,
      })
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load goal data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
          Team Goal Progress
        </h3>
        {!cyclesLoading && cycles.length > 0 && (
          <select
            value={selectedCycleId}
            onChange={(e) => setSelectedCycleId(e.target.value)}
            style={{
              fontSize: '12px',
              color: '#6B7280',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              padding: '4px 8px',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              width: '200px',
            }}
          >
            {cycles.map((cycle) => (
              <option key={cycle.value} value={cycle.value}>
                {cycle.label}
              </option>
            ))}
          </select>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'none' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div
              style={{
                width: '24px',
                height: '24px',
                border: '3px solid #E5E7EB',
                borderTopColor: '#4B45E5',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : (
          <MyTeamGoalsList data={data} cycleId={selectedCycleId} />
        )}
      </div>
    </div>
  );
};

// ============================================
// Team Performance Trendline Widget (Container)
// ============================================
const TeamPerformanceTrendlineWidget: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState<string>(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: Array<{ field: string; operator: string; value: string }> = [];
    if (dateFrom) {
      filters.push({ field: 'cycle_start_date_from', operator: '>=', value: dateFrom });
    }
    if (dateTo) {
      filters.push({ field: 'cycle_end_date_until', operator: '<=', value: dateTo });
    }

    analyticsService
      .fetchWidgetData({
        metric_key: 'team_performance_trendline',
        ...(filters.length > 0 ? { filters } : {}),
        limit: 1000,
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load performance trendline data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [dateFrom, dateTo]);

  const dateInputStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#6B7280',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    padding: '4px 8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    maxWidth: '140px',
  };

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
          Team Performance Trendline
        </h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={dateInputStyle}
            title="From date"
          />
          <span style={{ fontSize: '12px', color: '#9CA3AF' }}>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            min={dateFrom || undefined}
            style={dateInputStyle}
            title="To date"
          />
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{ height: '100%', opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s ease' }}>
              <TeamPerformanceTrendline data={data} />
            </div>
            {loading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WidgetSpinner />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// 9-Box Matrix Widget (Container)
// ============================================
const NineBoxMatrixContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gridCycles, setGridCycles] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedGridCycleId, setSelectedGridCycleId] = useState<string>('');
  const [gridCyclesLoading, setGridCyclesLoading] = useState(true);

  // Fetch grid cycles
  useEffect(() => {
    let cancelled = false;
    setGridCyclesLoading(true);

    analyticsFilterService
      .getFilterOptions('grid_cycle_id')
      .then((response) => {
        if (!cancelled && response.options.length > 0) {
          setGridCycles(response.options);
          setSelectedGridCycleId(response.options[0].value);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load grid cycles:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setGridCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch 9-box data when grid cycle changes
  useEffect(() => {
    if (!selectedGridCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: Array<{ field: string; operator: string; value: string }> = [
      { field: 'grid_cycle_id', operator: '=', value: selectedGridCycleId },
    ];

    analyticsService
      .fetchWidgetData({
        metric_key: '9box_talent_distribution',
        limit: 100,
        ...(filters.length > 0 ? { filters } : {}),
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load 9-Box matrix data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedGridCycleId]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <NineBoxMatrixWidget
        data={data}
        loading={loading}
        error={error || undefined}
        cycleOptions={gridCycles}
        selectedCycleId={selectedGridCycleId}
        onCycleChange={setSelectedGridCycleId}
        cycleOptionsLoading={gridCyclesLoading}
      />
    </div>
  );
};

// ============================================
// Review Completion by Department Widget (Container)
// ============================================
const ReviewCompletionByDeptContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewCycles, setReviewCycles] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch review cycles
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);

    analyticsFilterService
      .getFilterOptions('standard_review_cycle_id')
      .then((response) => {
        if (!cancelled && response.options.length > 0) {
          setReviewCycles(response.options);
          setSelectedCycleId(response.options[0].value);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load review cycles:', err);
      })
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch data when cycle changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: Array<{ field: string; operator: string; value: string }> = [
      { field: 'standard_review_cycle_id', operator: '=', value: selectedCycleId },
    ];

    analyticsService
      .fetchWidgetData({
        metric_key: 'review_completion_by_department',
        limit: 50,
        ...(filters.length > 0 ? { filters } : {}),
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load review completion data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <ReviewCompletionByDeptWidget
        data={data}
        loading={loading}
        error={error || undefined}
        cycleOptions={reviewCycles}
        selectedCycleId={selectedCycleId}
        onCycleChange={setSelectedCycleId}
        cycleOptionsLoading={cyclesLoading}
      />
    </div>
  );
};

// ============================================
// Competency Gap Analysis Widget (Container)
// ============================================
const CompetencyGapContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewCycles, setReviewCycles] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch review cycles
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);

    analyticsFilterService
      .getFilterOptions('standard_review_cycle_id')
      .then((response) => {
        if (!cancelled && response.options.length > 0) {
          setReviewCycles(response.options);
          setSelectedCycleId(response.options[0].value);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load review cycles:', err);
      })
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch data when cycle changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: Array<{ field: string; operator: string; value: string }> = [
      { field: 'standard_review_cycle_id', operator: '=', value: selectedCycleId },
    ];

    analyticsService
      .fetchWidgetData({
        metric_key: 'competency_gap_analysis',
        limit: 50,
        ...(filters.length > 0 ? { filters } : {}),
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load competency gap data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <CompetencyGapWidget
        data={data}
        loading={loading}
        error={error || undefined}
        cycleOptions={reviewCycles}
        selectedCycleId={selectedCycleId}
        onCycleChange={setSelectedCycleId}
        cycleOptionsLoading={cyclesLoading}
      />
    </div>
  );
};

// ============================================
// Feedback Flow Metrics Widget (Container)
// ============================================
const FeedbackFlowContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsService
      .fetchWidgetData({
        metric_key: 'feedback_flow_metrics',
        limit: 1,
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load feedback flow data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <FeedbackFlowWidget
        data={data}
        loading={loading}
        error={error || undefined}
      />
    </div>
  );
};

// ============================================
// Engagement Score Trends Widget (Container)
// ============================================
const EngagementScoreTrendsContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsService
      .fetchWidgetData({ metric_key: 'engagement_score_trends', limit: 24 })
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load engagement trends data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <EngagementScoreTrendsWidget
        data={data}
        loading={loading}
        error={error || undefined}
      />
    </div>
  );
};

// ============================================
// eNPS Sentiment Breakdown Widget (Container)
// ============================================
const EngagementPulseRadarContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [surveys, setSurveys] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');
  const [surveysLoading, setSurveysLoading] = useState(true);

  // Fetch survey options
  useEffect(() => {
    let cancelled = false;
    setSurveysLoading(true);

    analyticsFilterService
      .getFilterOptions('survey_id')
      .then((response) => {
        if (!cancelled && response.options.length > 0) {
          setSurveys(response.options);
          setSelectedSurveyId(response.options[0].value);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load surveys:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setSurveysLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch sentiment data when survey changes
  useEffect(() => {
    if (!selectedSurveyId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: Array<{ field: string; operator: string; value: string }> = [
      { field: 'survey_id', operator: '=', value: selectedSurveyId },
    ];

    analyticsService
      .fetchWidgetData({
        metric_key: 'enps_sentiment_breakdown',
        limit: 10,
        ...(filters.length > 0 ? { filters } : {}),
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load eNPS sentiment data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedSurveyId]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <EngagementPulseRadarWidget
        data={data}
        loading={loading}
        error={error || undefined}
        surveyOptions={surveys}
        selectedSurveyId={selectedSurveyId}
        onSurveyChange={setSelectedSurveyId}
        surveyOptionsLoading={surveysLoading}
      />
    </div>
  );
};

// ============================================
// Rating Calibration View Widget (Container)
// ============================================
const RatingCalibrationContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewCycles, setReviewCycles] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch only cycles with calibration data (manager has 3+ rated reports)
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);

    analyticsFilterService
      .getFilterOptions('calibration_review_cycle_id')
      .then((response) => {
        if (!cancelled && response.options.length > 0) {
          setReviewCycles(response.options);
          setSelectedCycleId(response.options[0].value);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load review cycles:', err);
      })
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch data when cycle changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: Array<{ field: string; operator: string; value: string }> = [
      { field: 'calibration_review_cycle_id', operator: '=', value: selectedCycleId },
    ];

    analyticsService
      .fetchWidgetData({
        metric_key: 'rating_calibration_view',
        limit: 10,
        ...(filters.length > 0 ? { filters } : {}),
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load rating calibration data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <RatingCalibrationWidget
        data={data}
        loading={loading}
        error={error || undefined}
        cycleOptions={reviewCycles}
        selectedCycleId={selectedCycleId}
        onCycleChange={setSelectedCycleId}
        cycleOptionsLoading={cyclesLoading}
      />
    </div>
  );
};

// ============================================
// Team Learning Plan Widget (Container)
// ============================================
const TeamLearningPathsWidget: React.FC = () => {
  const { data: rawData, loading, error } = useReviewCloudData({
    metricKey: 'review_api:learning_participants',
    queryConfig: {
      data_source: 'review_cloud',
      endpoints: {
        pending: '/learning-participants',
      },
    },
    chartType: 'learning_paths',
  });

  const data = (rawData as Record<string, unknown>[]) || [];

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
          Team Learning Plan
        </h3>
        {!loading && !error && (
          <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
            {data.length} participant{data.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'none' }}>
        {loading ? (
          <WidgetSpinner />
        ) : error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : (
          <TeamLearningPathsList data={data} />
        )}
      </div>
    </div>
  );
};

// ============================================
// Static Tab Content Components
// ============================================

// Team Learn widget metric keys (matching Trainery-Analytics registry_learn.go)
const TEAM_LEARN_WIDGET_CONFIG = {
  completion: { metricKey: 'learn_team_completion', title: 'Team Course Completion' },
  overdue: { metricKey: 'learn_team_overdue', title: 'Team Overdue Courses' },
  events: { metricKey: 'learn_team_event_attendance', title: 'Team Event Attendance' },
  coaching: { metricKey: 'learn_team_coaching_snapshot', title: 'Team Coaching Snapshot' },
};

type TeamView = 'perform' | 'learn';

const ViewToggle: React.FC<{ activeView: 'perform' | 'learn'; onToggle: (v: 'perform' | 'learn') => void }> = ({ activeView, onToggle }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: '#F3F4F6',
        borderRadius: '10px',
        padding: '3px',
      }}
    >
      <button
        onClick={() => onToggle('perform')}
        style={{
          padding: '6px 16px',
          fontSize: '13px',
          fontWeight: activeView === 'perform' ? '600' : '500',
          color: activeView === 'perform' ? '#4B45E5' : '#6B7280',
          backgroundColor: activeView === 'perform' ? '#FFFFFF' : 'transparent',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: activeView === 'perform' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
          whiteSpace: 'nowrap',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        PerformSpark
      </button>
      <button
        onClick={() => onToggle('learn')}
        style={{
          padding: '6px 16px',
          fontSize: '13px',
          fontWeight: activeView === 'learn' ? '600' : '500',
          color: activeView === 'learn' ? '#4B45E5' : '#6B7280',
          backgroundColor: activeView === 'learn' ? '#FFFFFF' : 'transparent',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: activeView === 'learn' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
          whiteSpace: 'nowrap',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        TraineryLearn
      </button>
    </div>
  </div>
);

const TeamLearnView: React.FC<{
  completionData: any; completionLoading: boolean; completionError: string | null;
  overdueData: any; overdueLoading: boolean; overdueError: string | null;
  eventsData: any; eventsLoading: boolean; eventsError: string | null;
  coachingData: any; coachingLoading: boolean; coachingError: string | null;
}> = (props) => (
  <div className="grid grid-cols-2 gap-4" style={{ flex: 1 }}>
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" style={{ height: '320px' }}>
      <TeamCourseCompletionWidget data={props.completionData} loading={props.completionLoading} error={props.completionError || undefined} />
    </div>
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" style={{ height: '320px' }}>
      <TeamOverdueCoursesWidget data={props.overdueData} loading={props.overdueLoading} error={props.overdueError || undefined} />
    </div>
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" style={{ height: '320px' }}>
      <TeamEventAttendanceWidget data={props.eventsData} loading={props.eventsLoading} error={props.eventsError || undefined} />
    </div>
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" style={{ height: '320px' }}>
      <TeamCoachingSnapshotWidget data={props.coachingData} loading={props.coachingLoading} error={props.coachingError || undefined} />
    </div>
  </div>
);

const TeamDashboardTab: React.FC = () => {
  const [activeView, setActiveView] = useState<TeamView>('perform');
  const { user } = useAuth();
  const isSubscriptionLoading = useSubscriptionStore((s) => s.isLoading);
  const isModuleEnabled = useSubscriptionStore((s) => s.isModuleEnabled);
  const isModuleActive = useSubscriptionStore((s) => s.isModuleActive);
  const isCustomerUser = (user as any)?.user_category === 'customer_employee';
  const hasLearn = isSubscriptionLoading || isCustomerUser || (isModuleEnabled('learn') && isModuleActive('learn'));

  // Fetch team learn widget data — only if Learn is subscribed
  const { data: completionData, loading: completionLoading, error: completionError } = useAnalyticsData({
    metricKey: TEAM_LEARN_WIDGET_CONFIG.completion.metricKey,
    enabled: hasLearn,
  });
  const { data: overdueData, loading: overdueLoading, error: overdueError } = useAnalyticsData({
    metricKey: TEAM_LEARN_WIDGET_CONFIG.overdue.metricKey,
    enabled: hasLearn,
  });
  const { data: eventsData, loading: eventsLoading, error: eventsError } = useAnalyticsData({
    metricKey: TEAM_LEARN_WIDGET_CONFIG.events.metricKey,
    enabled: hasLearn,
  });
  const { data: coachingData, loading: coachingLoading, error: coachingError } = useAnalyticsData({
    metricKey: TEAM_LEARN_WIDGET_CONFIG.coaching.metricKey,
    enabled: hasLearn,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px' }}>
      {hasLearn && <ViewToggle activeView={activeView} onToggle={setActiveView} />}
      {(activeView === 'perform' || !hasLearn) ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', minWidth: 0 }}>
          <div style={{ height: '320px', minWidth: 0, overflow: 'hidden' }}><MyTeamWidget /></div>
          <div style={{ height: '320px', minWidth: 0, overflow: 'hidden' }}><MyTeamGoalsWidget /></div>
          <div style={{ height: '320px', minWidth: 0, overflow: 'hidden' }}><TeamLearningPathsWidget /></div>
          <div style={{ height: '320px', minWidth: 0, overflow: 'hidden' }}><TeamPerformanceTrendlineWidget /></div>
        </div>
      ) : (
        <TeamLearnView
          completionData={completionData}
          completionLoading={completionLoading}
          completionError={completionError}
          overdueData={overdueData}
          overdueLoading={overdueLoading}
          overdueError={overdueError}
          eventsData={eventsData}
          eventsLoading={eventsLoading}
          eventsError={eventsError}
          coachingData={coachingData}
          coachingLoading={coachingLoading}
          coachingError={coachingError}
        />
      )}
    </div>
  );
};

// ============================================
// Unit Performance Widget (Container)
// ============================================
const UnitPerformanceWidget: React.FC = memo(() => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeLevels, setScopeLevels] = useState<Array<{ scopeLevelName: string }>>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [levelsLoading, setLevelsLoading] = useState(true);

  // Fetch available scope levels
  useEffect(() => {
    let cancelled = false;
    setLevelsLoading(true);

    analyticsService
      .fetchWidgetData({ metric_key: 'unit_scope_levels' })
      .then((response) => {
        if (!cancelled) {
          const levels = (response.data as Record<string, unknown>[]).map((row) => ({
            scopeLevelName: (row.scopeLevelName as string) ?? '',
          }));
          setScopeLevels(levels);
        }
      })
      .catch(() => { })
      .finally(() => {
        if (!cancelled) setLevelsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch performance data (re-fetch when level changes)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const queryConfig: Record<string, unknown> = {
      metric_key: 'unit_performance_overview',
      limit: 100,
    };
    if (selectedLevel) {
      queryConfig.filters = [
        { field: 'scope_level_name', operator: '=', value: selectedLevel },
      ];
    }

    analyticsService
      .fetchWidgetData(queryConfig as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load performance data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedLevel]);

  const title = selectedLevel
    ? `${selectedLevel} Performance Overview`
    : 'Performance Overview';

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
          {title}
        </h3>
        {!levelsLoading && scopeLevels.length > 0 && (
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            style={{
              fontSize: '12px',
              color: '#6B7280',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              padding: '4px 8px',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              width: '200px',
            }}
          >
            <option value="">All Levels</option>
            {scopeLevels.map((level) => (
              <option key={level.scopeLevelName} value={level.scopeLevelName}>
                {level.scopeLevelName}
              </option>
            ))}
          </select>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{ height: '100%', opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s ease' }}>
              <UnitPerformanceChart data={data} />
            </div>
            {loading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WidgetSpinner />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

// ============================================
// Review Progress Widget (Container)
// ============================================
const ReviewProgressWidget: React.FC = memo(() => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<Array<{ cycleId: string; cycleName: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch active cycles
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);

    analyticsService
      .fetchWidgetData({ metric_key: 'unit_active_cycles' })
      .then((response) => {
        if (!cancelled) {
          const rows = (response.data as Record<string, unknown>[]).map((row) => ({
            cycleId: String(row.cycleId ?? ''),
            cycleName: (row.cycleName as string) ?? '',
          }));
          setCycles(rows);
          if (rows.length > 0) {
            setSelectedCycleId(rows[0].cycleId);
          }
        }
      })
      .catch(() => { })
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch review progress when cycle changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsService
      .fetchWidgetData({
        metric_key: 'unit_review_progress',
        filters: [{ field: 'cycle_id', operator: '=', value: selectedCycleId }],
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load review progress');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId]);

  const selectedCycleName = cycles.find((c) => c.cycleId === selectedCycleId)?.cycleName ?? '';

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexShrink: 0 }}>
        <div>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
            Review Progress Tracker
          </h3>
          {selectedCycleName && (
            <span style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px', display: 'block' }}>
              {selectedCycleName}
            </span>
          )}
        </div>
        {!cyclesLoading && cycles.length > 0 && (
          <select
            value={selectedCycleId}
            onChange={(e) => setSelectedCycleId(e.target.value)}
            style={{
              fontSize: '12px',
              color: '#6B7280',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              padding: '4px 8px',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              width: '200px',
            }}
          >
            {cycles.map((cycle) => (
              <option key={cycle.cycleId} value={cycle.cycleId}>
                {cycle.cycleName}
              </option>
            ))}
          </select>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{ height: '100%', opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s ease' }}>
              <ReviewProgressChart data={data} />
            </div>
            {loading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WidgetSpinner />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

// ============================================
// Goal Progress Widget (Container)
// ============================================
const GoalProgressWidget: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<Array<{ cycleId: string; cycleName: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);
  const [scopeLevels, setScopeLevels] = useState<Array<{ scopeLevelName: string }>>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [levelsLoading, setLevelsLoading] = useState(true);

  // Fetch goal cycles and scope levels in parallel
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);
    setLevelsLoading(true);

    analyticsService
      .fetchWidgetData({ metric_key: 'unit_goal_cycles' })
      .then((response) => {
        if (!cancelled) {
          const rows = (response.data as Record<string, unknown>[]).map((row) => ({
            cycleId: String(row.cycleId ?? ''),
            cycleName: (row.cycleName as string) ?? '',
          }));
          setCycles(rows);
          if (rows.length > 0) {
            setSelectedCycleId(rows[0].cycleId);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    analyticsService
      .fetchWidgetData({ metric_key: 'unit_scope_levels' })
      .then((response) => {
        if (!cancelled) {
          const levels = (response.data as Record<string, unknown>[]).map((row) => ({
            scopeLevelName: (row.scopeLevelName as string) ?? '',
          }));
          setScopeLevels(levels);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLevelsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch goal progress when cycle or level changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: Array<{ field: string; operator: string; value: string }> = [
      { field: 'goal_cycle_id', operator: '=', value: selectedCycleId },
    ];
    if (selectedLevel) {
      filters.push({ field: 'scope_level_name', operator: '=', value: selectedLevel });
    }

    analyticsService
      .fetchWidgetData({
        metric_key: 'unit_goal_progress',
        filters,
        limit: 50,
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load goal progress');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId, selectedLevel]);

  const title = selectedLevel
    ? `${selectedLevel} Goal Progress`
    : 'Goal Progress by Team';

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
          {title}
        </h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {!levelsLoading && scopeLevels.length > 0 && (
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              style={{
                fontSize: '12px',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '4px 8px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                width: '200px',
              }}
            >
              <option value="">All Levels</option>
              {scopeLevels.map((level) => (
                <option key={level.scopeLevelName} value={level.scopeLevelName}>
                  {level.scopeLevelName}
                </option>
              ))}
            </select>
          )}
          {!cyclesLoading && cycles.length > 0 && (
            <select
              value={selectedCycleId}
              onChange={(e) => setSelectedCycleId(e.target.value)}
              style={{
                fontSize: '12px',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '4px 8px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                width: '200px',
              }}
            >
              {cycles.map((cycle) => (
                <option key={cycle.cycleId} value={cycle.cycleId}>
                  {cycle.cycleName}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{ height: '100%', opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s ease' }}>
              <GoalProgressChart data={data} />
            </div>
            {loading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WidgetSpinner />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// Feedback Activity Widget (Container)
// ============================================
const FeedbackActivityWidget: React.FC = memo(() => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<Array<{ cycleId: string; cycleName: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch 360-enabled cycles
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);

    analyticsService
      .fetchWidgetData({ metric_key: 'unit_360_cycles' })
      .then((response) => {
        if (!cancelled) {
          const rows = (response.data as Record<string, unknown>[]).map((row) => ({
            cycleId: String(row.cycleId ?? ''),
            cycleName: (row.cycleName as string) ?? '',
          }));
          setCycles(rows);
          if (rows.length > 0) {
            setSelectedCycleId(rows[0].cycleId);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch feedback activity when cycle changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsService
      .fetchWidgetData({
        metric_key: 'unit_feedback_activity',
        filters: [{ field: 'cycle_id', operator: '=', value: selectedCycleId }],
      } as any)
      .then((response) => {
        if (!cancelled) {
          setData(response.data as Record<string, unknown>[]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load feedback activity');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId]);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
          Feedback Activity
        </h3>
        {!cyclesLoading && cycles.length > 0 && (
          <select
            value={selectedCycleId}
            onChange={(e) => setSelectedCycleId(e.target.value)}
            style={{
              fontSize: '12px',
              color: '#6B7280',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              padding: '4px 8px',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              width: '200px',
            }}
          >
            {cycles.map((cycle) => (
              <option key={cycle.cycleId} value={cycle.cycleId}>
                {cycle.cycleName}
              </option>
            ))}
          </select>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{ height: '100%', opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s ease' }}>
              <FeedbackActivityChart data={data} />
            </div>
            {loading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WidgetSpinner />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

const UnitDashboardTab: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', minWidth: 0 }}>
      <div style={{ height: '320px', minWidth: 0, overflow: 'hidden' }}><UnitPerformanceWidget /></div>
      <div style={{ height: '320px', minWidth: 0, overflow: 'hidden' }}><ReviewProgressWidget /></div>
      <div style={{ height: '320px', minWidth: 0, overflow: 'hidden' }}><GoalProgressWidget /></div>
      <div style={{ height: '320px', minWidth: 0, overflow: 'hidden' }}><FeedbackActivityWidget /></div>
    </div>
  </div>
);

// ============================================
// Org Learn Widget Containers (same pattern as PerformSpark org containers)
// ============================================
const orgLearnContainerStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  height: '100%',
  display: 'flex' as const,
  flexDirection: 'column' as const,
  overflow: 'hidden' as const,
};

const OrgKPISummaryContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsService
      .fetchWidgetData({ metric_key: 'learn_org_kpi_summary', limit: 1 })
      .then((response) => { if (!cancelled) setData(response.data as Record<string, unknown>[]); })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load KPI data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={orgLearnContainerStyle}>
      <OrgKPISummaryWidget data={data as any} loading={loading} error={error || undefined} />
    </div>
  );
};

const OrgChannelMixContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsService
      .fetchWidgetData({ metric_key: 'learn_org_channel_mix', limit: 10 })
      .then((response) => { if (!cancelled) setData(response.data as Record<string, unknown>[]); })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load channel mix data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={orgLearnContainerStyle}>
      <OrgChannelMixWidget data={data as any} loading={loading} error={error || undefined} />
    </div>
  );
};

const OrgTopCoursesContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsService
      .fetchWidgetData({ metric_key: 'learn_org_top_courses', limit: 5 })
      .then((response) => { if (!cancelled) setData(response.data as Record<string, unknown>[]); })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load top courses data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={orgLearnContainerStyle}>
      <OrgTopCoursesWidget data={data as any} loading={loading} error={error || undefined} />
    </div>
  );
};

const OrgCompletionByCategoryContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsService
      .fetchWidgetData({ metric_key: 'learn_org_completion_by_category', limit: 10 })
      .then((response) => { if (!cancelled) setData(response.data as Record<string, unknown>[]); })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load category data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={orgLearnContainerStyle}>
      <OrgCompletionByCategoryWidget data={data as any} loading={loading} error={error || undefined} />
    </div>
  );
};

const OrgTopLearnersContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsService
      .fetchWidgetData({ metric_key: 'learn_org_top_learners', limit: 10 })
      .then((response) => { if (!cancelled) setData(response.data as Record<string, unknown>[]); })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load top learners data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={orgLearnContainerStyle}>
      <OrgTopLearnersWidget data={data as any} loading={loading} error={error || undefined} />
    </div>
  );
};

const OrgILTUtilizationContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsService
      .fetchWidgetData({ metric_key: 'learn_org_ilt_utilization', limit: 20 })
      .then((response) => { if (!cancelled) setData(response.data as Record<string, unknown>[]); })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load ILT data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={orgLearnContainerStyle}>
      <OrgILTUtilizationWidget data={data as any} loading={loading} error={error || undefined} />
    </div>
  );
};

const OrgCertificateIssuanceContainer: React.FC = () => {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsService
      .fetchWidgetData({ metric_key: 'learn_org_certificate_issuance', limit: 10 })
      .then((response) => { if (!cancelled) setData(response.data as Record<string, unknown>[]); })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load certificate data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={orgLearnContainerStyle}>
      <OrgCertificateIssuanceWidget data={data as any} loading={loading} error={error || undefined} />
    </div>
  );
};

type OrgView = 'perform' | 'learn';

const OrganizationDashboardTab: React.FC = () => {
  const [activeView, setActiveView] = useState<OrgView>('perform');
  const { user } = useAuth();
  const isSubscriptionLoading = useSubscriptionStore((s) => s.isLoading);
  const isModuleEnabled = useSubscriptionStore((s) => s.isModuleEnabled);
  const isModuleActive = useSubscriptionStore((s) => s.isModuleActive);
  const isCustomerUser = (user as any)?.user_category === 'customer_employee';
  const hasLearn = isSubscriptionLoading || isCustomerUser || (isModuleEnabled('learn') && isModuleActive('learn'));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#FFFFFF' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: '24px', backgroundColor: '#FFFFFF' }}>
        {hasLearn && <ViewToggle activeView={activeView} onToggle={setActiveView} />}
        {(activeView === 'perform' || !hasLearn) ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            <div style={{ height: '320px' }}><NineBoxMatrixContainer /></div>
            <div style={{ height: '320px' }}><ReviewCompletionByDeptContainer /></div>
            <div style={{ height: '320px' }}><RatingCalibrationContainer /></div>
            <div style={{ height: '320px' }}><EngagementPulseRadarContainer /></div>
            <div style={{ height: '320px' }}><CompetencyGapContainer /></div>
            <div style={{ height: '320px' }}><FeedbackFlowContainer /></div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            <div style={{ gridColumn: 'span 2', height: '180px' }}><OrgKPISummaryContainer /></div>
            <div style={{ height: '320px' }}><OrgChannelMixContainer /></div>
            <div style={{ height: '320px' }}><OrgTopCoursesContainer /></div>
            <div style={{ height: '320px' }}><OrgCompletionByCategoryContainer /></div>
            <div style={{ height: '320px' }}><OrgTopLearnersContainer /></div>
            <div style={{ height: '320px' }}><OrgILTUtilizationContainer /></div>
            <div style={{ height: '320px' }}><OrgCertificateIssuanceContainer /></div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Goal Cascade Types & Helpers
// ============================================
interface CascadeGoal {
  goalId: string;
  title: string;
  progress: number;
  okrScore: number | null;
  goalLevel: 'company' | 'team';
  parentGoalId: string | null;
  status: string;
}

interface CascadeTree extends CascadeGoal {
  children: CascadeGoal[];
}

const TEAM_GOAL_COLORS = [
  { bar: ChartColor.blue },
  { bar: ChartColor.emerald },
  { bar: ChartColor.amber },
  { bar: ChartColor.violet },
  { bar: ChartColor.red },
  { bar: ChartColor.cyan },
];

function buildCascadeTree(goals: CascadeGoal[]): CascadeTree[] {
  const companyGoals = goals.filter((g) => g.goalLevel === 'company');
  const teamGoals = goals.filter((g) => g.goalLevel === 'team');
  return companyGoals.map((company) => ({
    ...company,
    children: teamGoals.filter((t) => t.parentGoalId === company.goalId),
  }));
}

// ============================================
// Goal Cascade Progress Bar
// ============================================
const CascadeProgressBar: React.FC<{ progress: number; color: string }> = memo(
  ({ progress, color }) => (
    <div
      style={{
        width: '100%',
        height: '6px',
        backgroundColor: '#F3F4F6',
        borderRadius: '3px',
        overflow: 'hidden',
        marginTop: '6px',
      }}
    >
      <div
        style={{
          width: `${Math.min(100, Math.max(0, progress))}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius: '3px',
          transition: 'width 0.6s ease',
        }}
      />
    </div>
  )
);

// ============================================
// Team Goal Row
// ============================================
const TeamGoalRow: React.FC<{ goal: CascadeGoal; colorIndex: number }> = memo(
  ({ goal, colorIndex }) => {
    const palette = TEAM_GOAL_COLORS[colorIndex % TEAM_GOAL_COLORS.length];
    return (
      <div
        style={{
          padding: '10px 0',
          marginLeft: '28px',
          borderBottom: '1px solid #F3F4F6',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
            {goal.title}
          </span>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginLeft: '12px', flexShrink: 0 }}>
            {goal.progress}%
          </span>
        </div>
        <CascadeProgressBar progress={goal.progress} color={palette.bar} />
      </div>
    );
  }
);

// ============================================
// Company Goal Card (with nested team goals)
// ============================================
const CompanyGoalCard: React.FC<{ node: CascadeTree; baseColorIndex: number }> = memo(
  ({ node, baseColorIndex }) => {
    const [expanded, setExpanded] = useState(true);

    return (
      <div
        style={{
          borderBottom: '1px solid #E5E7EB',
          paddingBottom: '12px',
          marginBottom: '12px',
        }}
      >
        {/* Company goal header */}
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: node.children.length > 0 ? 'pointer' : 'default' }}
          onClick={() => node.children.length > 0 && setExpanded((p) => !p)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            {node.children.length > 0 && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 14 14"
                fill="none"
                style={{
                  flexShrink: 0,
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <path d="M5 3L9 7L5 11" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
              {node.title}
            </span>
          </div>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginLeft: '12px', flexShrink: 0 }}>
            {node.progress}%
          </span>
        </div>

        <CascadeProgressBar progress={node.progress} color={ChartColor.indigo} />

        {/* Team goals */}
        {expanded && node.children.length > 0 && (
          <div style={{ marginTop: '4px' }}>
            {node.children.map((child, idx) => (
              <TeamGoalRow key={child.goalId} goal={child} colorIndex={baseColorIndex + idx} />
            ))}
          </div>
        )}
      </div>
    );
  }
);

// ============================================
// Goal Cascade Widget (Container)
// ============================================
const GoalCascadeWidget: React.FC = memo(() => {
  const [goals, setGoals] = useState<CascadeGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<Array<{ cycleId: string; cycleName: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch goal cycles
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);

    analyticsService
      .fetchWidgetData({ metric_key: 'exec_goal_cycles' })
      .then((response) => {
        if (!cancelled) {
          const rows = (response.data as Record<string, unknown>[]).map((row) => ({
            cycleId: String(row.cycleId ?? ''),
            cycleName: (row.cycleName as string) ?? '',
          }));
          setCycles(rows);
          if (rows.length > 0) {
            setSelectedCycleId(rows[0].cycleId);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Fetch goal cascade when cycle changes
  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsService
      .fetchWidgetData({
        metric_key: 'exec_goal_cascade',
        filters: [{ field: 'goal_cycle_id', operator: '=', value: selectedCycleId }],
      } as any)
      .then((response) => {
        if (!cancelled) {
          const raw = response.data as Record<string, unknown>[];
          setGoals(
            raw.map((r) => ({
              goalId: String(r.goalId ?? ''),
              title: (r.title as string) ?? '',
              progress: Number(r.progress ?? 0),
              okrScore: r.okrScore != null ? Number(r.okrScore) : null,
              goalLevel: (r.goalLevel as 'company' | 'team') ?? 'team',
              parentGoalId: r.parentGoalId != null ? String(r.parentGoalId) : null,
              status: (r.status as string) ?? '',
            }))
          );
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.error || 'Failed to load goal cascade');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCycleId]);

  const tree = useMemo(() => buildCascadeTree(goals), [goals]);

  let colorCounter = 0;

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#111827', margin: 0 }}>
          Goal Alignment
        </h3>
        {!cyclesLoading && cycles.length > 0 && (
          <select
            value={selectedCycleId}
            onChange={(e) => setSelectedCycleId(e.target.value)}
            style={{
              fontSize: '12px',
              color: '#6B7280',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              padding: '4px 8px',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              maxWidth: '200px',
            }}
          >
            {cycles.map((c) => (
              <option key={c.cycleId} value={c.cycleId}>
                {c.cycleName}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', position: 'relative' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>
            {error}
          </div>
        ) : loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <WidgetSpinner />
          </div>
        ) : tree.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>
            No goals found for this cycle
          </div>
        ) : (
          tree.map((node) => {
            const base = colorCounter;
            colorCounter += node.children.length;
            return <CompanyGoalCard key={node.goalId} node={node} baseColorIndex={base} />;
          })
        )}
      </div>
    </div>
  );
});

// ============================================
// Rating Comparison Widget (Container)
// ============================================
interface RatingComparisonRow {
  scopeInstanceId: string;
  scopeInstanceName: string;
  scopeLevelName: string;
  avgManagerRating: number;
  avgEmployeeRating: number;
  employeeCount: number;
}

const RatingComparisonTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; payload: RatingComparisonRow }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '1px solid #E5E7EB', maxWidth: '220px' }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '6px' }}>{row.scopeLevelName} &middot; {row.employeeCount} employees</div>
      {payload.map((entry) => (
        <div key={entry.name} style={{ fontSize: '12px', color: '#6B7280', padding: '2px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: entry.color, flexShrink: 0 }} />
          {entry.name}: <span style={{ color: '#111827', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{entry.value.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

const dropdownStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6B7280',
  border: '1px solid #E5E7EB',
  borderRadius: '6px',
  padding: '4px 8px',
  backgroundColor: 'white',
  cursor: 'pointer',
  outline: 'none',
  width: '200px',
};

const RatingComparisonWidget: React.FC = memo(() => {
  const [data, setData] = useState<RatingComparisonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scope levels dropdown
  const [scopeLevels, setScopeLevels] = useState<Array<{ logicalScopeId: string; scopeLevelName: string }>>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [levelsLoading, setLevelsLoading] = useState(true);

  // Cycle dropdown
  const [cycles, setCycles] = useState<Array<{ cycleId: string; cycleName: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  // Fetch scope levels and active cycles on mount (parallel)
  useEffect(() => {
    let cancelled = false;
    setLevelsLoading(true);
    setCyclesLoading(true);

    analyticsService
      .fetchWidgetData({ metric_key: 'exec_scope_levels' })
      .then((response) => {
        if (!cancelled) {
          setScopeLevels(
            (response.data as Record<string, unknown>[]).map((row) => ({
              logicalScopeId: String(row.logicalScopeId ?? ''),
              scopeLevelName: (row.scopeLevelName as string) ?? '',
            }))
          );
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLevelsLoading(false); });

    analyticsService
      .fetchWidgetData({ metric_key: 'exec_active_cycles' })
      .then((response) => {
        if (!cancelled) {
          const rows = (response.data as Record<string, unknown>[]).map((row) => ({
            cycleId: String(row.cycleId ?? ''),
            cycleName: (row.cycleName as string) ?? '',
          }));
          setCycles(rows);
          if (rows.length > 0) setSelectedCycleId(rows[0].cycleId);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCyclesLoading(false); });

    return () => { cancelled = true; };
  }, []);

  // Fetch rating comparison data when cycle or level changes
  useEffect(() => {
    if (!selectedCycleId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: Array<{ field: string; operator: string; value: string }> = [
      { field: 'cycle_id', operator: '=', value: selectedCycleId },
    ];
    if (selectedLevel) {
      filters.push({ field: 'scope_level_name', operator: '=', value: selectedLevel });
    }

    analyticsService
      .fetchWidgetData({ metric_key: 'exec_rating_comparison', filters } as any)
      .then((response) => {
        if (!cancelled) {
          setData(
            (response.data as Record<string, unknown>[]).map((r) => ({
              scopeInstanceId: String(r.scopeInstanceId ?? ''),
              scopeInstanceName: (r.scopeInstanceName as string) ?? 'Unknown',
              scopeLevelName: (r.scopeLevelName as string) ?? '',
              avgManagerRating: Number(r.avgManagerRating ?? 0),
              avgEmployeeRating: Number(r.avgEmployeeRating ?? 0),
              employeeCount: Number(r.employeeCount ?? 0),
            }))
          );
        }
      })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load rating comparison'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [selectedCycleId, selectedLevel]);

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header with dropdowns */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>Rating Comparison</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {!levelsLoading && scopeLevels.length > 0 && (
            <select value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)} style={dropdownStyle}>
              <option value="">All Levels</option>
              {scopeLevels.map((l) => (
                <option key={l.logicalScopeId} value={l.scopeLevelName}>{l.scopeLevelName}</option>
              ))}
            </select>
          )}
          {!cyclesLoading && cycles.length > 0 && (
            <select value={selectedCycleId} onChange={(e) => setSelectedCycleId(e.target.value)} style={dropdownStyle}>
              {cycles.map((c) => (
                <option key={c.cycleId} value={c.cycleId}>{c.cycleName}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Chart */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>{error}</div>
        ) : (
          <>
            <div style={{ height: '100%', opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s ease' }}>
              {data.length === 0 && !loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>No rating data for this selection</div>
              ) : (() => {
                const RC_MIN_BAR_WIDTH = 90;
                const rcNeedsScroll = data.length > 10;
                const rcScrollWidth = rcNeedsScroll ? data.length * RC_MIN_BAR_WIDTH : undefined;
                const RC_Y_WIDTH = 36;

                const rcXAxisTick = (props: any) => {
                  const label = props.payload?.value ?? '';
                  const x = Number(props.x);
                  const y = Number(props.y);
                  const maxChars = 12;
                  const words = label.split(' ');
                  const lines: string[] = [];
                  let current = '';
                  for (const word of words) {
                    if (current && (current + ' ' + word).length > maxChars) {
                      lines.push(current);
                      current = word;
                    } else {
                      current = current ? current + ' ' + word : word;
                    }
                  }
                  if (current) lines.push(current);
                  const truncatedLines = lines.slice(0, 2).map((line: string, i: number) => {
                    if (i === 1 && lines.length > 2) {
                      return line.length > 10 ? line.slice(0, 10) + '…' : line + '…';
                    }
                    return line;
                  });
                  return (
                    <text x={x} y={y + 12} textAnchor="middle" fill="#6B7280" fontSize={11}>
                      {truncatedLines.map((line: string, i: number) => (
                        <tspan key={i} x={x} dy={i === 0 ? 0 : 14}>{line}</tspan>
                      ))}
                    </text>
                  );
                };

                const rcBarChart = (hideY?: boolean) => (
                  <BarChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 40 }} barGap={2} barCategoryGap="20%" maxBarSize={48}>
                    <CartesianGrid stroke={hideY ? getChartGridColor('grid') : 'transparent'} strokeDasharray="none" vertical={false} horizontal={!hideY ? false : true} />
                    <XAxis
                      dataKey="scopeInstanceName"
                      tick={hideY ? rcXAxisTick : (() => <text />) as any}
                      axisLine={hideY ? { stroke: getChartGridColor('axis') } : false}
                      tickLine={false}
                      interval={0}
                    />
                    {hideY ? (
                      <YAxis hide />
                    ) : (
                      <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={{ stroke: getChartGridColor('axis') }} tickLine={false} width={RC_Y_WIDTH} />
                    )}
                    {hideY && <Tooltip content={<RatingComparisonTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />}
                    <Bar dataKey="avgManagerRating" name="Manager Rating" fill={hideY ? ChartColor.blue : 'transparent'} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="avgEmployeeRating" name="Self Rating" fill={hideY ? ChartColor.emerald : 'transparent'} radius={[4, 4, 0, 0]} />
                  </BarChart>
                );

                const rcLegend = (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', fontSize: '12px', color: '#6B7280', paddingTop: '4px', flexShrink: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '10px', height: '10px', backgroundColor: ChartColor.blue, borderRadius: '2px', display: 'inline-block' }} />
                      Manager Rating
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '10px', height: '10px', backgroundColor: ChartColor.emerald, borderRadius: '2px', display: 'inline-block' }} />
                      Self Rating
                    </span>
                  </div>
                );

                if (!rcNeedsScroll) {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <div style={{ flex: 1, minHeight: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 40 }} barGap={2} barCategoryGap="20%" maxBarSize={48}>
                            <CartesianGrid stroke={getChartGridColor('grid')} strokeDasharray="none" vertical={false} />
                            <XAxis
                              dataKey="scopeInstanceName"
                              tick={rcXAxisTick}
                              axisLine={{ stroke: getChartGridColor('axis') }}
                              tickLine={false}
                              interval={0}
                            />
                            <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={{ stroke: getChartGridColor('axis') }} tickLine={false} width={RC_Y_WIDTH} />
                            <Tooltip content={<RatingComparisonTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                            <Bar dataKey="avgManagerRating" name="Manager Rating" fill={ChartColor.blue} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="avgEmployeeRating" name="Self Rating" fill={ChartColor.emerald} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {rcLegend}
                    </div>
                  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
                      {/* Fixed Y-axis */}
                      <div style={{ width: RC_Y_WIDTH + 8, flexShrink: 0, height: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {rcBarChart(false)}
                        </ResponsiveContainer>
                      </div>
                      {/* Scrollable bar area */}
                      <div className="no-scrollbar" style={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden', height: '100%', scrollbarWidth: 'none' }}>
                        <div style={{ minWidth: rcScrollWidth, height: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            {rcBarChart(true)}
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                    {rcLegend}
                  </div>
                );
              })()}
            </div>
            {loading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><WidgetSpinner /></div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

// ============================================
// Review Cycle Status Widget (Container)
// ============================================
interface CycleStatusData {
  totalParticipants: number;
  selfReviewCompleted: number;
  selfReviewPct: number;
  managerReviewCompleted: number;
  managerReviewPct: number;
  hrReviewCompleted: number;
  hrReviewPct: number;
}

const ReviewCycleStatusWidget: React.FC = memo(() => {
  const [data, setData] = useState<CycleStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<Array<{ cycleId: string; cycleName: string }>>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cyclesLoading, setCyclesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);
    analyticsService
      .fetchWidgetData({ metric_key: 'exec_review_cycles' })
      .then((response) => {
        if (!cancelled) {
          const rows = (response.data as Record<string, unknown>[]).map((row) => ({
            cycleId: String(row.cycleId ?? ''),
            cycleName: (row.cycleName as string) ?? '',
          }));
          setCycles(rows);
          if (rows.length > 0) setSelectedCycleId(rows[0].cycleId);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCyclesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedCycleId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    analyticsService
      .fetchWidgetData({
        metric_key: 'exec_review_cycle_status',
        filters: [{ field: 'cycle_id', operator: '=', value: selectedCycleId }],
      } as any)
      .then((response) => {
        if (!cancelled) {
          const rows = response.data as Record<string, unknown>[];
          if (rows.length > 0) {
            const r = rows[0];
            setData({
              totalParticipants: Number(r.totalParticipants ?? r.total_participants ?? 0),
              selfReviewCompleted: Number(r.selfReviewCompleted ?? r.self_review_completed ?? 0),
              selfReviewPct: Number(r.selfReviewPct ?? r.self_review_pct ?? 0),
              managerReviewCompleted: Number(r.managerReviewCompleted ?? r.manager_review_completed ?? 0),
              managerReviewPct: Number(r.managerReviewPct ?? r.manager_review_pct ?? 0),
              hrReviewCompleted: Number(r.hrReviewCompleted ?? r.hr_review_completed ?? 0),
              hrReviewPct: Number(r.hrReviewPct ?? r.hr_review_pct ?? 0),
            });
          } else {
            setData(null);
          }
        }
      })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load cycle status'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCycleId]);

  const selectedCycleName = cycles.find((c) => c.cycleId === selectedCycleId)?.cycleName ?? '';

  const stages = data ? [
    { label: 'Self Review', completed: data.selfReviewCompleted, total: data.totalParticipants, pct: data.selfReviewPct, color: ChartColor.indigo },
    { label: 'Manager Review', completed: data.managerReviewCompleted, total: data.totalParticipants, pct: data.managerReviewPct, color: ChartColor.violet },
    { label: 'HR Review', completed: data.hrReviewCompleted, total: data.totalParticipants, pct: data.hrReviewPct, color: '#A78BFA' },
  ] : [];

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>Review Cycle Status</h3>
        {!cyclesLoading && cycles.length > 0 ? (
          <select value={selectedCycleId} onChange={(e) => setSelectedCycleId(e.target.value)} style={{ fontSize: '12px', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '4px 8px', backgroundColor: 'white', cursor: 'pointer', outline: 'none', width: '200px' }}>
            {cycles.map((c) => (<option key={c.cycleId} value={c.cycleId}>{c.cycleName}</option>))}
          </select>
        ) : selectedCycleName ? (
          <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{selectedCycleName}</span>
        ) : null}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>{error}</div>
        ) : loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><WidgetSpinner /></div>
        ) : !data ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>No cycle data available</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827' }}>{data.totalParticipants}</div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>Total Participants</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {stages.map((stage) => (
                <div key={stage.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', color: '#374151' }}>{stage.label}</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{stage.completed}<span style={{ fontSize: '11px', fontWeight: 400, color: '#9CA3AF' }}>/{stage.total}</span></span>
                  </div>
                  <div style={{ height: '6px', backgroundColor: '#F3F4F6', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${stage.pct}%`, backgroundColor: stage.color, borderRadius: '3px', transition: 'width 0.3s ease' }} />
                  </div>
                  <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>{stage.pct}%</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

// ============================================
// Engagement Pulse Widget (Container)
// ============================================
const EngagementPulseWidget: React.FC = memo(() => {
  const [data, setData] = useState<{ enps: number; change: number; promoters: number; passives: number; detractors: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    analyticsService
      .fetchWidgetData({ metric_key: 'exec_engagement_pulse' })
      .then((response) => {
        if (!cancelled) {
          const row = (response.data as Record<string, unknown>[])[0];
          if (row) {
            setData({
              enps: Number(row.enps ?? row.enps_score ?? row.enpsScore ?? 0),
              change: Number(row.change ?? row.enps_trend ?? row.enpsTrend ?? 0),
              promoters: Number(row.promoters ?? row.promoter_pct ?? row.promoterPct ?? 0),
              passives: Number(row.passives ?? row.passive_pct ?? row.passivePct ?? 0),
              detractors: Number(row.detractors ?? row.detractor_pct ?? row.detractorPct ?? 0),
            });
          }
        }
      })
      .catch((err) => { if (!cancelled) setError(err?.error || 'Failed to load engagement data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ marginBottom: '16px', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>Engagement Pulse</h3>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#EF4444', fontSize: '13px' }}>{error}</div>
        ) : loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><WidgetSpinner /></div>
        ) : data ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            {/* eNPS Score */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '40px', fontWeight: 700, color: '#111827', lineHeight: 1 }}>{data.enps.toFixed(1)}</div>
              <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '6px' }}>eNPS Score</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: data.change >= 0 ? getSentimentColor('positive') : getSentimentColor('negative'), marginTop: '6px' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d={data.change >= 0 ? 'M6 2.5L10 7.5H2L6 2.5Z' : 'M6 9.5L2 4.5H10L6 9.5Z'} fill="currentColor" />
                </svg>
                {Math.abs(data.change).toFixed(1)} vs last quarter
              </div>
            </div>

            {/* Stacked bar */}
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', width: '100%' }}>
                {data.promoters > 0 && <div style={{ width: `${data.promoters}%`, backgroundColor: getSentimentColor('positive'), transition: 'width 0.3s ease' }} />}
                {data.passives > 0 && <div style={{ width: `${data.passives}%`, backgroundColor: '#D1D5DB', transition: 'width 0.3s ease' }} />}
                {data.detractors > 0 && <div style={{ width: `${data.detractors}%`, backgroundColor: getSentimentColor('negative'), transition: 'width 0.3s ease' }} />}
              </div>
            </div>

            {/* Legend row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getSentimentColor('positive'), flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: '#374151' }}>Promoters</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{data.promoters}%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#D1D5DB', flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: '#374151' }}>Passive</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{data.passives}%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getSentimentColor('negative'), flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: '#374151' }}>Detractors</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{data.detractors}%</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>No engagement data</div>
        )}
      </div>
    </div>
  );
});

// ============================================
// Executive Dashboard Tab (4 widgets, 2-col grid, fixed row height)
// ============================================
const EXEC_ROW_HEIGHT = 'calc((100vh - 200px) / 2)';

const ExecutiveDashboardTab: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <div style={{ padding: '16px 16px 16px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridAutoRows: EXEC_ROW_HEIGHT, gap: '16px', flex: 1, overflow: 'auto' }}>
      <GoalCascadeWidget />
      <RatingComparisonWidget />
      <ReviewCycleStatusWidget />
      <EngagementPulseWidget />
    </div>
  </div>
);

// ============================================
// HomePage Component
// ============================================
const HomePage: React.FC = () => {
  const { isSuperAdmin, isManager, isScopedHRBP, isGlobalHRBP, isExecutive } = useUserAccess();
  const navigate = useNavigate();
  const location = useLocation();

  // Subscription check — overview tabs require PerformSpark
  const { user } = useAuth();
  const isSubscriptionLoading = useSubscriptionStore((s) => s.isLoading);
  const isModuleEnabled = useSubscriptionStore((s) => s.isModuleEnabled);
  const isModuleActive = useSubscriptionStore((s) => s.isModuleActive);
  const isCustomerUser = (user as any)?.user_category === 'customer_employee';
  const hasPerform = isSubscriptionLoading || isCustomerUser || (isModuleEnabled('perform-spark') && isModuleActive('perform-spark'));

  // Derive active tab from URL path
  const activeTab = useMemo(() => {
    const match = location.pathname.match(/\/home\/(.+)/);
    return match ? match[1] : '';
  }, [location.pathname]);

  // Redirect bare /home to /home/my-portal
  useEffect(() => {
    if (!activeTab) {
      navigate('/home/my-portal', { replace: true });
    }
  }, [activeTab, navigate]);

  // Determine which overview tabs the user can access (exclude my-portal — it's always shown as the default content)
  const overviewTabs = useMemo(() => {
    const flags = { isManager, isScopedHRBP, isGlobalHRBP, isExecutive, isSuperAdmin };
    return TAB_CONFIG.filter((tab) => {
      if (tab.id === 'my-portal') return false; // My Portal is the default, not a tab
      if (!hasPerform) return false; // Overview tabs require PerformSpark
      return tab.accessCheck(flags);
    });
  }, [isManager, isScopedHRBP, isGlobalHRBP, isExecutive, isSuperAdmin, hasPerform]);

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'my-portal':
        return <MyPortalPage />;
      case 'team-overview':
        return <TeamDashboardTab />;
      case 'unit-overview':
        return <UnitDashboardTab />;
      case 'organization-overview':
        return <OrganizationDashboardTab />;
      case 'executive-overview':
        return <ExecutiveDashboardTab />;
      default:
        return <MyPortalPage />;
    }
  };

  return (
    <div className="home-dashboard" style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'white' }}>
      <style>{spinKeyframes}</style>
      {/* Header with tabs — only show when there are overview tabs */}
      {overviewTabs.length > 0 && (
        <div
          style={{
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: 'white',
            flexShrink: 0,
          }}
        >
          {/* Tab bar */}
          <div
            style={{
              display: 'flex',
              gap: '0',
              padding: '0 24px',
              marginTop: '0',
            }}
          >
            {/* My Portal tab — always first */}
            <button
              onClick={() => navigate('/home/my-portal')}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: activeTab === 'my-portal' ? '600' : '500',
                color: activeTab === 'my-portal' ? '#4B45E5' : '#6B7280',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'my-portal' ? '2px solid #4B45E5' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                fontFamily: 'system-ui, -apple-system, sans-serif',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'my-portal') {
                  e.currentTarget.style.color = '#4B45E5';
                  e.currentTarget.style.borderBottomColor = '#C7D2FE';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'my-portal') {
                  e.currentTarget.style.color = '#6B7280';
                  e.currentTarget.style.borderBottomColor = 'transparent';
                }
              }}
            >
              My Portal
            </button>
            {overviewTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => navigate(`/home/${tab.id}`)}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: isActive ? '600' : '500',
                    color: isActive ? '#4B45E5' : '#6B7280',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: isActive ? '2px solid #4B45E5' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = '#4B45E5';
                      e.currentTarget.style.borderBottomColor = '#C7D2FE';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = '#6B7280';
                      e.currentTarget.style.borderBottomColor = 'transparent';
                    }
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default HomePage;
